import { useMemo } from 'react';
import { clsx } from 'clsx';
import type { Item } from '@perfin/shared';
import { isRecurringItem } from '@perfin/shared';
import { CalendarItemTile } from './CalendarItemTile';

interface WeeklyViewProps {
  items: Item[];
  offset: number;
  basePath?: string; // Optional - kept for backward compatibility but CalendarItemTile handles routing
  showItemType?: boolean; // Show item type icon (useful for search results with mixed types)
}

interface DayItem {
  item: Item;
  isUnscheduled: boolean; // true if placed by createdAt instead of dueDate
  occurrenceDate?: Date;  // For recurring items, which occurrence this tile represents
}

interface DayColumn {
  date: Date;
  dayName: string;
  dayNumber: number;
  isToday: boolean;
  items: DayItem[];
}

// Helper to safely parse dates from Timestamp or Date objects
function parseDate(d: unknown): Date | null {
  if (!d) return null;
  if (typeof (d as { toDate?: () => Date }).toDate === 'function') {
    return (d as { toDate: () => Date }).toDate();
  }
  if (d instanceof Date) return d;
  if (typeof d === 'object' && 'seconds' in d) {
    return new Date((d as { seconds: number }).seconds * 1000);
  }
  return new Date(d as string | number);
}

export function WeeklyView({ items, offset, showItemType = false }: WeeklyViewProps) {
  const weekDays = useMemo((): DayColumn[] => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Calculate week start (Monday) based on offset
    const currentDay = now.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() + mondayOffset + offset * 7);
    weekStart.setHours(0, 0, 0, 0);

    const days: DayColumn[] = [];
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);

      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      // Filter items for this day
      // - Recurring items: check occurrences[] array and create tile per occurrence
      // - Non-recurring items: use dueDate (or createdAt as fallback)
      const dayItems: DayItem[] = [];

      items.forEach((item) => {
        // Check if recurring item
        if (isRecurringItem(item)) {
          // Check occurrences that fall on this day
          const occurrences = (item as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences;
          if (!occurrences || occurrences.length === 0) return;

          occurrences.forEach((occ) => {
            const occDate = parseDate(occ.date);
            if (!occDate) return;
            if (occDate >= dayStart && occDate <= dayEnd) {
              dayItems.push({
                item,
                isUnscheduled: false,
                occurrenceDate: occDate,
              });
            }
          });
          return;
        }

        // Non-recurring: use dueDate
        const dueDate = parseDate(item.dueDate);
        if (dueDate && dueDate >= dayStart && dueDate <= dayEnd) {
          dayItems.push({
            item,
            isUnscheduled: false,
          });
          return;
        }

        // No dueDate - use createdAt
        if (!dueDate) {
          const createdDate = parseDate(item.createdAt);
          if (createdDate && createdDate >= dayStart && createdDate <= dayEnd) {
            dayItems.push({
              item,
              isUnscheduled: true,
            });
          }
        }
      });

      days.push({
        date,
        dayName: dayNames[i],
        dayNumber: date.getDate(),
        isToday: date.getTime() === today.getTime(),
        items: dayItems,
      });
    }

    return days;
  }, [items, offset]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header row */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {weekDays.map((day) => (
          <div
            key={day.date.toISOString()}
            className={clsx(
              'px-2 py-3 text-center border-r border-gray-200 last:border-r-0',
              day.isToday && 'bg-primary-50'
            )}
          >
            <div className="text-xs font-medium text-gray-500 uppercase">{day.dayName}</div>
            <div
              className={clsx(
                'text-lg font-semibold mt-1',
                day.isToday ? 'text-primary-600' : 'text-gray-900'
              )}
            >
              {day.dayNumber}
            </div>
          </div>
        ))}
      </div>

      {/* Content row */}
      <div className="grid grid-cols-7 min-h-[300px]">
        {weekDays.map((day) => (
          <div
            key={day.date.toISOString()}
            className={clsx(
              'p-2 border-r border-gray-200 last:border-r-0',
              day.isToday && 'bg-primary-50/30'
            )}
          >
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              {day.items.length === 0 ? (
                <div className="text-xs text-gray-400 text-center py-4">No items</div>
              ) : (
                day.items.map(({ item, isUnscheduled, occurrenceDate }) => (
                  <CalendarItemTile
                    key={occurrenceDate ? `${item.id}-${occurrenceDate.getTime()}` : item.id}
                    item={item}
                    variant="default"
                    showType={showItemType}
                    isUnscheduled={isUnscheduled}
                    occurrenceDate={occurrenceDate}
                  />
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
